/**
 * Constants
 */

/**
 * These arguments are known to contain text values
 * We have a hardcoded list of them here because we cannot currently deduce this information from the metadata
 * If new textual arguments are added, be sure they're added to this list
 */
export const TEXT_ARGUMENTS = {
  directory: true,
  ipAddresses: true,
  device: true,
  hostnames: true,
  egressPorts: true,
  sourcePorts: true,
  process: true,
  group: true,
  user: true
}

/**
 * These arguments are known to be numerical values
 * We have a hardcoded list of them here because we cannot currently deduce this information from the metadata
 * If new numerical arguments are added, be sure they're added to this list
 *
 * This is here so that we avoid any false positives in the future if new argument types are added.
 * Unless they appear in this list, they will not be considered numerical values,
 * and will not be treated as such, avoiding potential bugs with math calcs on non-numerical values.
 */
export const NUMERICAL_ARGUMENTS = {
  delay: true,
  length: true,
  percent: true,
  megabytes: true,
  gigabytes: true,
  workers: true,
  blockSize: true,
  blockCount: true,
  interval: true,
  cores: true,
  offset: true,
  threadCount: true
}

/**
 * Utility functions
 */

/**
 * Make argument values pretty for display
 *
 * @param arg
 * @returns {{name: *, magnitude: (null|*), value: *}}
 */
export function formatArgForDisplay(arg) {
  return {
    name: arg.name,
    value: arg.isBoolean ? (arg.value ? 'Yes' : 'No') : arg.value,
    magnitude: arg.isBoolean || !arg.magnitude ? 0 : arg.magnitude
  }
}

/**
 * Move the primary args for the attack type into the iconArgs list, and the rest into the overflowArgs list
 *
 * @param attackType string The key for this type of attack
 * @param args array All arguments for this attack
 * @param magnitudeArgCounts object A map of each type of attack arg used in this scenario and how many times it's used
 * @param numIconArgs number how many icon args to display
 * @returns {{overflowArgs: [], iconArgs: []}}
 */
export function separateArgumentsForDisplay(
  attackType,
  args,
  magnitudeArgCounts,
  numIconArgs = 2
) {
  let iconArgFields = []
  const iconArgs = []
  const overflowArgs = []

  if (!args || args.length < 1) {
    return {
      iconArgs,
      overflowArgs
    }
  }

  switch (String(attackType).toLowerCase()) {
    case 'cpu':
      iconArgFields = ['percent', 'cores']
      break

    case 'disk':
      iconArgFields = ['percent']
      break

    case 'io':
      iconArgFields = ['workers', 'mode']
      break

    case 'memory':
      // memory has three different arguments to select the amount of memory
      // but only one of these will be used at a time, so this will only pick one of these
      iconArgFields = ['percent', 'gigabytes', 'megabytes']
      break

    case 'shutdown':
      iconArgFields = ['reboot']
      break

    case 'time_travel':
      iconArgFields = ['offset']
      break

    case 'latency':
      iconArgFields = ['delay', 'protocol']
      break

    case 'packet_loss':
      iconArgFields = ['percent', 'protocol']
      break

    case 'process_exhaustion':
      iconArgFields = ['percent', 'megabytes']
      break

    case 'process_killer':
    case 'blackhole':
    case 'dns':
    default:
      break
  }

  // separate the primary arguments as icons
  iconArgFields.forEach((field) => {
    // only add to icons if there's room left
    if (iconArgs.length < numIconArgs) {
      const iconArg = args.filter((curArg) => curArg.key === field)[0]

      if (iconArg) {
        iconArgs.push(formatArgForDisplay(iconArg))
      }
    }
  })

  // move any others into the overflow
  args
    .filter((arg) => !iconArgFields.includes(arg.key))
    .forEach((arg) => {
      // if we have room for more icon args, but there have been none added use the first overflow args
      // so long as they're not arbitrary text content
      if (iconArgs.length < numIconArgs && !TEXT_ARGUMENTS[arg.key]) {
        iconArgs.push(formatArgForDisplay(arg))
      } else {
        overflowArgs.push(formatArgForDisplay(arg))
      }
    })

  // now that we know how many times each arg is used, let's also pick out the most used arg
  // and we'll always display that one first
  // this way it will consistently have the same color and be more easily-distinguishable in the list
  let mostUsedArg
  let maxArgCount = 0
  Object.keys(magnitudeArgCounts).forEach((arg) => {
    if (magnitudeArgCounts[arg] > maxArgCount) {
      maxArgCount = magnitudeArgCounts[arg]
      mostUsedArg = arg
    }
  })

  // now re-order the icon args so the most used one comes first
  // but only if there's actually more than one attack
  // and the most used one is not first
  if (
    maxArgCount > 1 &&
    iconArgs.length > 1 &&
    iconArgs[0].name !== mostUsedArg
  ) {
    // swap the two args
    const otherArg = iconArgs[0]
    iconArgs[0] = iconArgs[1]
    iconArgs[1] = otherArg
  }

  return {
    iconArgs,
    overflowArgs
  }
}

/**
 * Process all attacks' arguments and derive the max value for each argument in this scenario
 * This will later be used to derive the percentage (magnitude) of the current argument
 *
 * @param attackArgs
 * @returns {{}}
 */
export function getMaxArgValues(attackArgs) {
  if (!Array.isArray(attackArgs)) return {}

  return (
    attackArgs
      // extract all args from all attacks into one long list of args
      .flat()
      // extract only the arguments known to have numerical values
      .filter((argument) => NUMERICAL_ARGUMENTS[argument.key])
      // and finally process the numerical arguments and determine max values for them
      .reduce((maxValues, argument) => {
        const argValue = Number(argument.value)
        const curMax = !maxValues[argument.key] ? 0 : maxValues[argument.key]
        maxValues[argument.key] = Math.max(argValue, curMax)
        return maxValues
      }, {})
  )
}

/**
 * Calculate the magnitude (percentage) of the current arg value compared to the max value of this arg for all attacks
 *
 * @param args array
 * @param maxValues array
 * @returns {array}
 */
export function adornMagnitudePercentage(args, maxValues) {
  return args.map((arg) => {
    arg.magnitude = maxValues[arg.key]
      ? Number(Math.round((arg.value / maxValues[arg.key]) * 100))
      : 0
    return arg
  })
}
